import { Fragment, useState } from "react";

import { Alert, Breadcrumb, Button, Page, Typography } from "@eisox/design-system";
import { NutIcon, SunIcon } from "@eisox/icons";
import { createFileRoute, Link } from "@tanstack/react-router";

import { HistoryDatesSlider } from "~/components/history-dates-slider";
import * as LiveHistoryToggle from "~/components/live-history-toggle";
import { Mode } from "~/components/live-history-toggle";
import { PositioningDialog, type Item } from "~/components/positioning-dialog";
import { RealtimeUnavailableDialog } from "~/components/realtime-unavailable-dialog";
import {
  ActionDropdownMenu,
  DisableLink,
  DisplayDropdownMenu,
  ErrorCollapsible,
  PlanningTabButton,
  type FocusRequest,
  RightColumn,
  Scheme,
  useAirHandlerUnitContext,
  useUpdateAirHandlerUnit,
} from "~/features/air-handler-unit";
import { LiveErrorDialog } from "~/features/real-time";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/air-handler-units/$airHandlerUnitId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { buildingId, airHandlerUnitId } = Route.useParams();
  const {
    building,
    floors,
    permissions,
    modules,
    airHandlerUnits: buildingAirHandlerUnits,
  } = useBuildingContext(Route.id);
  const {
    airHandlerUnit,
    airHandlerUnits,
    floors: airHandlerUnitFloors,
    name,
    errors,
    mode,
    onModeChange,
    isSnapshotLoading,
    isHistoryDatesLoading,
    isRealtimeStale,
    liveSampleDate,
    date,
    days,
    dates,
    onDateChange,
    onReload,
  } = useAirHandlerUnitContext(Route.id);

  const { mutate } = useUpdateAirHandlerUnit(building.id);

  const [focusRequest, setFocusRequest] = useState<FocusRequest>();

  const [positioningDialogOpen, setPositioningDialogOpen] = useState(
    airHandlerUnits.filter(a => a.floor === undefined).length > 0 &&
      !!permissions.module?.airHandlerUnit?.floor?.update,
  );

  const handleSubmit = (data: Item[]) => {
    data.forEach(item => {
      mutate({ airHandlerUnitId: item.id, body: { floorId: item.floor?.id, x: item.floor?.x, y: item.floor?.y } });
    });
  };

  const unitWithGatewayErrors = airHandlerUnit.find(ahu => ahu.errors?.length);
  const gatewayErrors = unitWithGatewayErrors?.errors;
  const moduleId = buildingAirHandlerUnits.find(ahu => ahu.id === unitWithGatewayErrors?.id)?.moduleId;
  const module = modules.find(m => m.id === moduleId);

  return (
    <Page.Root>
      <Page.Header className="flex-nowrap [&>:not(:first-child)]:shrink-0">
        <Breadcrumb.Root className="min-w-0 overflow-hidden">
          <Breadcrumb.List className="min-w-0">
            {airHandlerUnitFloors.length > 0 && (
              <>
                <Breadcrumb.Item className="shrink-0">
                  <Breadcrumb.Link asChild>
                    <div>
                      {airHandlerUnitFloors.map((floor, index) => (
                        <Fragment key={floor.id}>
                          <Link
                            to={"/buildings/$buildingId/floors/$floorId"}
                            params={{ buildingId, floorId: floor.id }}
                          >
                            {floor.name}
                          </Link>
                          {index < airHandlerUnitFloors.length - 1 && ", "}
                        </Fragment>
                      ))}
                    </div>
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator className="shrink-0" />
              </>
            )}
            <Breadcrumb.Item className="min-w-0">
              <Breadcrumb.Page asChild>
                <Page.Title asChild>
                  <Typography className="min-w-0">{name}</Typography>
                </Page.Title>
              </Breadcrumb.Page>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb.Root>
        <Button asChild disabled={mode === Mode.History}>
          <DisableLink
            to={"/buildings/$buildingId/air-handler-units/$airHandlerUnitId/settings/general"}
            params={{ buildingId, airHandlerUnitId }}
          >
            {m.settings()} <NutIcon />
          </DisableLink>
        </Button>

        <PlanningTabButton
          buildingId={buildingId}
          airHandlerUnitId={airHandlerUnitId}
          mode={mode}
          isRealtimeStale={isRealtimeStale}
        />
        <Button asChild>
          <Link to={"/buildings/$buildingId/settings/buildings/holidays"} params={{ buildingId: building.id }}>
            <div className={"size-4 rounded-xs bg-planning-frost-free"} />
            {m.frost_free_periods()}
          </Link>
        </Button>
        <Button asChild>
          <Link to={"/buildings/$buildingId/settings/buildings/season"} params={{ buildingId: building.id }}>
            <SunIcon />
            {m.season()}
          </Link>
        </Button>
        <DisplayDropdownMenu buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} />
        <ActionDropdownMenu buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} />
      </Page.Header>
      <Page.Content className="mb-10 ml-10 gap-5 md:mt-0 md:mr-0 md:grid-cols-[1fr_290px] grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] grid-rows-[auto_1fr] rounded-l-[10px] rounded-r-none bg-transparent [grid-template-areas:'scheme_error'_'scheme_column'_'scheme_liveHistoryToggle']">
        <div className="min-w-0 flex h-full w-full flex-col items-center justify-center rounded-[10px] bg-primary-white [grid-area:scheme]">
          {gatewayErrors && module ? (
            <Alert severity="error">
              <div className="flex flex-col">
                <Typography as="h3">{m.bms_modules_error()}</Typography>
                <LiveErrorDialog errors={gatewayErrors} module={module} airHandlerUnit={{ name }}>
                  <Button className="font-medium text-primary-white">{m.see_errors()}</Button>
                </LiveErrorDialog>
              </div>
            </Alert>
          ) : (
            <Scheme
              className="h-full w-full rounded-none bg-transparent"
              buildingId={buildingId}
              airHandlerUnit={airHandlerUnit}
              loading={isSnapshotLoading}
              focusRequest={focusRequest}
            />
          )}
          {mode === Mode.History && (
            <HistoryDatesSlider
              isLoading={isHistoryDatesLoading}
              days={days}
              dates={dates}
              date={date}
              onDateCommit={onDateChange}
            />
          )}
        </div>
        <ErrorCollapsible className="[grid-area:error]" buildingId={buildingId} errors={errors} />
        <RightColumn
          className="[grid-area:column]"
          buildingId={buildingId}
          name={name}
          airHandlerUnit={airHandlerUnit}
          airHandlerUnits={airHandlerUnits}
          onAirHandlerUnitFocus={id => setFocusRequest({ airHandlerUnitId: id })}
        />
        <LiveHistoryToggle.Root
          className="gap-2 p-3 md:p-5 flex flex-col items-center justify-center rounded-[10px] bg-primary-white [grid-area:liveHistoryToggle]"
          mode={mode}
          onModeChange={onModeChange}
        >
          <LiveHistoryToggle.Tabs />
          <div className="gap-2.5 flex items-center">
            <LiveHistoryToggle.Timestamp className="md:text-base text-sm whitespace-nowrap" date={date} />
            <LiveHistoryToggle.Reload onReload={onReload} />
          </div>
        </LiveHistoryToggle.Root>
      </Page.Content>
      <PositioningDialog
        open={positioningDialogOpen}
        onOpenChange={setPositioningDialogOpen}
        buildingId={buildingId}
        type="airHandlerUnits"
        items={airHandlerUnits}
        floors={floors}
        onSubmit={handleSubmit}
      />
      <RealtimeUnavailableDialog
        open={isRealtimeStale && !gatewayErrors?.length}
        buildingId={buildingId}
        lastSampleDate={liveSampleDate}
        onGoToHistory={() => onModeChange(Mode.History)}
      />
    </Page.Root>
  );
}
